import React, { useState, useCallback, useMemo } from 'react';
import EditIcon from '@/assets/icons/EditIcon';
import { WebsitePageSection } from '@/types';
import { useWebPageForm } from '@/contexts/WebPageFormContext';
import InlineTextEditor from '@/components/admin/Content/WebPages/InlineEditors/InlineTextEditor';
import InlineImageEditor from '@/components/admin/Content/WebPages/InlineEditors/InlineImageEditor';
import InlineFileEditor from '@/components/admin/Content/WebPages/InlineEditors/InlineFileEditor';
import InlineCredentialsEditor from '@/components/admin/Content/WebPages/InlineEditors/InlineCredentialsEditor';
import InlineListEditor from '@/components/admin/Content/WebPages/InlineEditors/InlineListEditor';

interface EditableSectionProps {
  sectionTag: string | null;
  contentType: string | null;
  data: WebsitePageSection;
  children: React.ReactNode;
  className?: string;
  customEditor?: React.ComponentType<any>;
}

const EditableSection: React.FC<EditableSectionProps> = ({
  sectionTag,
  contentType,
  data,
  children,
  customEditor
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { updateLocalSection, onSavePage } = useWebPageForm();

  // Select the appropriate editor based on contentType
  const EditorComponent = useMemo(() => {
    if (customEditor) return customEditor;

    const content = contentType || data.contentType;
    switch (content) {
      case 'WITH_FILE_ONLY':
        return InlineImageEditor;
      case 'WITH_FILE':
        return InlineFileEditor;
      case 'WITH_CREDENTIALS':
        return InlineCredentialsEditor;
      case 'WITH_LIST':
        return InlineListEditor;
      case 'WITHOUT_FILE':
      default:
        return InlineTextEditor;
    }
  }, [contentType, customEditor, data.contentType]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(async (updatedData: Partial<WebsitePageSection>) => {
    if (!sectionTag) return;

    const updatedPayload = {
      ...data,
      ...updatedData,
      contentType: contentType || data.contentType
    };

    updateLocalSection(sectionTag, updatedPayload);

    // Save entire page as draft
    if (onSavePage) {
      await onSavePage();
    }

    // Only close the editor AFTER successful save
    setIsEditing(false);
  }, [sectionTag, data, contentType, updateLocalSection, onSavePage]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);


  if (isEditing) {
    return (
      <div className={`relative border-2 border-dashed border-primary rounded-lg p-4 bg-white`}>
        <EditorComponent
          data={data}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div
      className="relative group h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleEdit}
    >
      {/* Hover overlay with border and edit icon */}
      {isHovered && (
        <div className="absolute inset-0 border-2 border-dashed border-gray-300 rounded-lg bg-white/50 backdrop-blur-sm z-[5] flex items-center justify-center transition-all duration-200">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-lg">
            <EditIcon className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-gray-700">Click to edit</span>
          </div>
        </div>
      )}

      {/* Original content */}
      <div className={isHovered ? 'opacity-60' : 'h-full'}>
        {children}
      </div>
    </div>
  );
};

export default EditableSection;

